import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { EnvironmentService } from './environment.service';
export interface ISessionTokens {
    idToken: string;
    accessToken: string;
    refreshToken?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private userInfo: any = null;
    IS_LOCALHOST = window.location.origin.includes('localhost');

    constructor(
        private http: HttpClient,
        private environmentService: EnvironmentService
    ) {}

    authorize(): void {
        this.http
            .get(`${this.environmentService.getAuthApiUrl()}/auth/actions/get-authorization-url`, {
                params: {
                    successPath: `${window.location.origin}${environment.authSuccessPath}`,
                    errorPath: `${window.location.origin}${environment.authErrorPath}`
                }
            })
            .subscribe((response: any) => {
                console.log('Auth endpoint response:', response);
                window.location.href = response.authorizationRedirectUrl;
            });
    }

    setSession(headerTokens: ISessionTokens): void {
        if (this.IS_LOCALHOST) {
            localStorage.setItem('access_token', headerTokens.accessToken);
            localStorage.setItem('id_token', headerTokens.idToken);
            localStorage.setItem('refresh_token', headerTokens.refreshToken!);
        }
    }

    getCurrentSession(): ISessionTokens | null {
        const accessToken = this.getCookie('access_token');
        const idToken = this.getCookie('id_token');

        if (!accessToken || !idToken) {
            return null;
        }

        let userSession: ISessionTokens = {
            accessToken,
            idToken
        };

        if (this.isTokenValid(userSession.accessToken) && this.isTokenValid(userSession.idToken)) {
            return userSession;
        } else {
            this.refreshTokens().subscribe((success) => {
                if (success) {
                    userSession = {
                        accessToken: this.getCookie('access_token')!,
                        idToken: this.getCookie('id_token')!
                    };
                    return userSession;
                } else {
                    window.location.href = `${window.location.origin}${environment.loginPath}`;
                    return;
                }
            });
        }

        return null;
    }

    getCurrentUser(): any {
        if (this.userInfo) {
            return this.userInfo;
        }

        const idToken = this.getCookie('id_token');
        if (!idToken) return null;

        try {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            this.userInfo = payload;
            return payload;
        } catch (error) {
            return null;
        }
    }

    getAccessTokenPayload(): any {
        const accessToken = this.getCookie('access_token');
        if (!accessToken) return null;

        try {
            return JSON.parse(atob(accessToken.split('.')[1]));
        } catch (error) {
            return null;
        }
    }

    getUserInfo(): Observable<any> {
        const token = this.getCookie('access_token');
        if (!token) return of(null);

        const headers = new HttpHeaders({
            Authorization: `Bearer ${token}`
        });

        return this.http.get(`${this.environmentService.getAuthApiUrl()}/users/me`, { headers }).pipe(
            map((userInfo: any) => {
                this.userInfo = userInfo;
                console.log('User info result:', userInfo);
                return userInfo;
            }),
            catchError((error) => {
                console.log(`Error: ${error}`);
                this.authorize();
                return of(null);
            })
        );
    }

    refreshTokens(): Observable<boolean> {
        console.log('Attempting to refresh tokens...');
        const refreshToken = this.getCookie('refresh_token');
        this.clearAccessTokens();

        let headers: HttpHeaders = new HttpHeaders();
        if (this.IS_LOCALHOST && refreshToken) {
            headers = new HttpHeaders({
                'x-refresh-token': refreshToken
            });
        }
        return this.http
            .get(`${this.environmentService.getAuthApiUrl()}/auth/actions/refresh`, {
                withCredentials: true,
                observe: 'response',
                headers: this.IS_LOCALHOST ? headers : undefined
            })
            .pipe(
                map((response) => {
                    console.log('Tokens refreshed successfully');

                    if (this.IS_LOCALHOST) {
                        const headers = response.headers;
                        const accessToken = headers.get('x-access-token');
                        const idToken = headers.get('x-id-token');
                        const refreshToken = headers.get('x-refresh-token');

                        localStorage.setItem('access_token', accessToken!);
                        localStorage.setItem('id_token', idToken!);
                        localStorage.setItem('refresh_token', refreshToken!);
                    }

                    return true;
                }),
                catchError((error) => {
                    console.log('Token refresh failed:', error);
                    return of(false);
                })
            );
    }

    clearAccessTokens(): void {
        if (this.IS_LOCALHOST) {
            ['id_token', 'access_token'].forEach((token) => {
                localStorage.removeItem(token);
            });
            return;
        }
        const hostname = window.location.hostname;
        const domain = hostname !== 'localhost' ? `; domain=.${hostname.split('.').slice(-2).join('.')}` : '';
        document.cookie = `access_token=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        document.cookie = `id_token=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }

    getCurrentUserSession(): Observable<any> {
        return this.http.get(`${this.environmentService.getAuthApiUrl()}/auth/session`).pipe(
            map((sessionData: any) => {
                console.log('Session data:', sessionData);
                return sessionData;
            }),
            catchError((error) => {
                console.error('Error fetching session:', error);
                console.error('Full error object:', error);
                return of(null);
            })
        );
    }

    logout(): void {
        if (this.IS_LOCALHOST) {
            ['id_token', 'access_token', 'refresh_token'].forEach((token) => {
                localStorage.removeItem(token);
            });
        } else {
            const hostname = window.location.hostname;
            const domain = hostname !== 'localhost' ? `; domain=.${hostname.split('.').slice(-2).join('.')}` : '';
            document.cookie = `access_token=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            document.cookie = `id_token=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            document.cookie = `refresh_token=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        }

        const redirectUrl = `${window.location.protocol}//${window.location.host}`;
        this.http.get(`${this.environmentService.getAuthApiUrl()}/auth/actions/logout?redirectUrl=${encodeURIComponent(redirectUrl)}`, { withCredentials: true }).subscribe((response: any) => {
            this.userInfo = null;
            window.location.href = response.redirectUrl;
        });
    }

    private getCookie(name: string): string | null {
        if (this.IS_LOCALHOST) {
            return localStorage.getItem(name);
        }
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
        return null;
    }

    isTokenValid(token: string | null): boolean {
        if (!token) {
            return false;
        }

        // Check if token has JWT format (3 parts separated by dots)
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.error('Token is not in JWT format:', token);
            return false;
        }

        try {
            const payload = JSON.parse(atob(parts[1]));
            const exp = payload.exp * 1000; // Convert to milliseconds
            return Date.now() < exp;
        } catch (error) {
            console.error('Error validating token:', error);
            return false;
        }
    }
}
